import ObjectPivotWrapper from "./object-pivot-wrapper.js";

const tmpVec = new THREE.Vector3();

const Events = [
    "grab", "release",
    "dragstart", "dragstop", "drag",
    "execute", "change",
];

class ObjRotCtrlsWrapper {
    constructor(objRotCtrl, pivotGetter) {
        if (objRotCtrl.enabled) {
            console.warn("object rotation controls are enabled but the wrapper assumes they are disabled");
        }

        this._objRotCtrl = objRotCtrl;
        this._pivotGetter = pivotGetter;

        this._events = new THREE.EventDispatcher();

        this._enabled = false;
        this._autoSpinObjs = new Set();

        this._onEventThis = this._onEvent.bind(this);
        this._objWrap = new WeakMap();
        this._objUnwrap = new WeakMap();
        this._selectObjWrap = new WeakMap();
        this._getDefRotGetWrap = new WeakMap();
        this._evtWrap = new WeakMap();

        this._evtProxyHandler = {
            set: (event, property, value, receiver) => {
                const { properties } = this._evtWrap.get(event);
                properties[property] = value;
                return true;
            },

            deleteProperty: (event, property) => {
                const { properties } = this._evtWrap.get(event);
                delete properties[property];
                return true;
            },

            has: (event, property) => {
                const { properties } = this._evtWrap.get(event);
                return property in properties || property in event;
            },

            get: (event, property, receiver) => {
                const { properties } = this._evtWrap.get(event);

                if (property in properties) {
                    return properties[property];
                } else {
                    const value = event[property];

                    if (property === "object") {
                        return this._objUnwrap.get(value);
                    } else {
                        return value;
                    }
                }
            },
        };
    }

    get events() {
        return this._events;
    }

    set enabled(v) {
        v = Boolean(v);
        if (v === this._enabled) return;

        this._enabled = v;
        this._objRotCtrl.enabled = v;

        if (v) {
            this._addListeners();

            for (const object of this._autoSpinObjs) {
                this._getObjWrapper(object); // this will trigger the pivot update
            }
        } else {
            this._removeListeners();
        }
    }

    _addListeners() {
        for (const name of Events) {
            this._objRotCtrl.events.addEventListener(name, this._onEventThis);
        }
    }

    _removeListeners() {
        for (const name of Events) {
            this._objRotCtrl.events.removeEventListener(name, this._onEventThis);
        }
    }

    get enabled() { return this._enabled; }
    get rotationMode() { return this._objRotCtrl.rotationMode; }
    set rotationMode(v) { this._objRotCtrl.rotationMode = v; }
    get object() { return this._objRotCtrl.object; }

    setObjectSelector(selectObject) {
        const wrapper = this._getObjSelectWrapper(selectObject);
        this._objRotCtrl.setObjectSelector(wrapper);
    }

    setDefaultRotationGetter(getObjectDefaultRotation) {
        const wrapper = this._getObjDefRotGetWrapper(getObjectDefaultRotation);
        this._objRotCtrl.setDefaultRotationGetter(wrapper);
    }

    startDragging(action, mousePos) { return this._objRotCtrl.startDragging(action, mousePos); }
    showSnackBar(message) { return this._objRotCtrl.showSnackBar(message); }
    stopDragging() { return this._objRotCtrl.stopDragging(); }
    drag(newMousePos) { return this._objRotCtrl.drag(newMousePos); }
    zoom(mousePos, delta) { return this._objRotCtrl.zoom(mousePos, delta); }
    update() { return this._objRotCtrl.update(); }
    clearObjectRotationSpaces() { return this._objRotCtrl.clearObjectRotationSpaces(); }

    setAutoSpin(object, enabled, speed) {
        if (enabled) {
            this._autoSpinObjs.add(object);
        } else {
            this._autoSpinObjs.delete(object);
        }

        const wrapper = this._getObjWrapper(object);
        return this._objRotCtrl.setAutoSpin(wrapper, enabled, speed);
    }

    _onEvent(event) {
        const wrapper = this._getEventWrapper(event);
        this._events.dispatchEvent(wrapper);
    }

    _getObjWrapper(object) {
        let wrapper = this._objWrap.get(object);

        if (!wrapper) {
            wrapper = new ObjectPivotWrapper();

            this._objWrap.set(object, wrapper);
            this._objUnwrap.set(wrapper, object);
        }

        const pivot = this._pivotGetter.getObjectPivot(object, tmpVec);
        wrapper.setObjectAndPivot(object, pivot);

        return wrapper;
    }

    _getObjSelectWrapper(selectObj) {
        let wrapper = this._selectObjWrap.get(selectObj);

        if (!wrapper) {
            wrapper = (mousePos) => {
                const object = selectObj(mousePos);
                const objWrap = object && this._getObjWrapper(object);
                return objWrap;
            };

            this._selectObjWrap.set(selectObj, wrapper);
        }

        return wrapper;
    }

    _getObjDefRotGetWrapper(getRotation) {
        let wrapper = this._getDefRotGetWrap.get(getRotation);

        if (!wrapper) {
            wrapper = (objWrap) => {
                const object = this._objUnwrap.get(objWrap);
                return getRotation(object);
            }

            this._getDefRotGetWrap.set(getRotation, wrapper);
        }

        return wrapper;
    }

    _getEventWrapper(event) {
        let wrapperData = this._evtWrap.get(event);

        if (!wrapperData) {
            wrapperData = {
                wrapper: new Proxy(event, this._evtProxyHandler),
                properties: {},
            };

            this._evtWrap.set(event, wrapperData);
        }

        return wrapperData.wrapper;
    }
}

export default ObjRotCtrlsWrapper;
